import { formatChatTimestamp, formatFullDateTime } from '@saaga/shared/utils/format.util'
import { memo, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

import {
  getNotificationPreview,
  getNotificationSubtitle,
  getNotificationTitle,
} from './notification.helper'
import type { Notification, NotificationClickHandler } from './notification.interface'
import { NotificationAvatar } from './notification-avatar.atom'
import { NotificationItemContent } from './notification-item-content.atom'

export interface NotificationItemProps {
  notification: Notification
  onClick?: NotificationClickHandler
  className?: string
}

const NotificationItemBase: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
  className,
}): ReactNode => {
  const handleClick = (): void => {
    onClick?.(notification)
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'notification-item flex w-full max-w-full box-border gap-3 p-3 cursor-pointer transition-colors text-left',
        'hover:bg-accent focus:bg-accent focus:outline-none rounded-md',
        !notification.isRead && 'bg-accent/50',
        className
      )}
    >
      <div className="flex-shrink-0 pt-0.5">
        <NotificationAvatar notification={notification} />
      </div>
      <NotificationItemContent
        title={getNotificationTitle(notification)}
        subtitle={getNotificationSubtitle(notification)}
        preview={getNotificationPreview(notification)}
        relativeTime={formatChatTimestamp(notification.timestamp)}
        fullDateTime={formatFullDateTime(notification.timestamp)}
      />
      {!notification.isRead && (
        <div className="flex-shrink-0 pt-2">
          <div className="notification-unread-dot size-2 rounded-full bg-destructive" />
        </div>
      )}
    </button>
  )
}

export const NotificationItem = memo(NotificationItemBase)

NotificationItem.displayName = 'NotificationItem'
